import React, { useEffect, useState } from "react";
import NewStack from "./newStack";
import NewDomain from "./newDomain";
import NewProj from "./newProj";
import { CircularProgress } from "@mui/material";
import SpDialog from "../../../globals/SpDialog";
import { arrayUnion, deleteField } from "firebase/firestore";
import { getDocs } from "../../../globals/services";

const AddNew = (props) => {
  const storage = props.firebase.getStorage();

  const [techData, setTechData] = useState({
    techCategory: {},
    name: "",
    searchKeywords: "",
    rank: 0,
    subcategory: "",
    show: false,
    showRank: 0,
    description: "",
    logoStorage: "",
    assessments: [],
  });
  const [newData, setNewData] = useState({
    category: {},
    name: "",
    searchKeywords: "",
    rank: 0,
    subcategory: "",
    show: false,
    showRank: 0,
    description: "",
    assessments: [],
  });
  const [logo, setLogo] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedAssessments, setSelectedAssessments] = useState([]);

  // Add sector selection state
  const [availableSectors, setAvailableSectors] = useState([]);
  const [selectedSectorForIndustry, setSelectedSectorForIndustry] =
    useState(null);
  const [loadingSectors, setLoadingSectors] = useState(false);

  const resetStates = (callback) => {
    setTechData({
      techCategory: {},
      name: "",
      searchKeywords: "",
      rank: 0,
      subcategory: "",
      show: false,
      showRank: 0,
      description: "",
      logoStorage: "",
      assessments: [],
    });
    setNewData({
      category: {},
      name: "",
      searchKeywords: "",
      rank: 0,
      subcategory: "",
      show: false,
      showRank: 0,
      description: "",
      assessments: [],
    });
    setLogo({});
    setSelectedAssessments([]);

    // Reset sector selection states
    setSelectedSectorForIndustry(null);
    setAvailableSectors([]);

    props.setOpenDialog(false);
    props.setIsEdit(false);

    // Execute callback after state reset
    setTimeout(() => {
      if (callback) callback();
    }, 300);
  };

  const showOptions = [
    { value: true, label: "true" },
    { value: false, label: "false" },
  ];

  // Function to fetch available sectors
  const fetchAvailableSectors = async () => {
    setLoadingSectors(true);
    try {
      const result = await getDocs("sectors", false, 100, "name");
      const sectorsData = result.docs.map((doc) => ({
        value: doc.id,
        label: doc.data().name,
        ref: doc.ref,
        data: doc.data(),
      }));
      setAvailableSectors(sectorsData);
    } catch (error) {
      console.error("Error fetching sectors:", error);
      setAvailableSectors([]);
    } finally {
      setLoadingSectors(false);
    }
  };

  useEffect(() => {
    if (!props.open) {
      // Reset all states when dialog closes
      setTechData({
        techCategory: {},
        name: "",
        searchKeywords: "",
        rank: 0,
        subcategory: "",
        show: false,
        showRank: 0,
        description: "",
        logoStorage: "",
        assessments: [],
      });
      setNewData({
        category: {},
        name: "",
        searchKeywords: "",
        rank: 0,
        subcategory: "",
        show: false,
        showRank: 0,
        description: "",
        assessments: [],
      });
      setLogo({});
      setSelectedAssessments([]);
      setSelectedSectorForIndustry(null);
      setAvailableSectors([]);
      setLoading(false);
    }
  }, [props.open]);

  // Fetch sectors when dialog opens for industry collection and no parent is selected
  useEffect(() => {
    if (
      props.open &&
      props.collection === "industry" &&
      !props.selectedSector
    ) {
      fetchAvailableSectors();
    }
  }, [props.open, props.collection, props.selectedSector]);

  useEffect(() => {
    if (props.isEdit && props.open) {
      // Only set data when dialog is open
      const { assessments, ...data } = props.defaultData;

      // Load full assessment data for editing
      const loadAssessments = async () => {
        if (assessments && assessments.length > 0) {
          try {
            const fullAssessments = await Promise.all(
              assessments.map(async (assessment) => {
                // If assessment already has data property, use it as is
                if (assessment.data) {
                  return assessment;
                }

                // Otherwise, fetch the full document data
                if (assessment.ref) {
                  const doc = await assessment.ref.get();
                  if (doc.exists) {
                    return {
                      id: doc.id,
                      ref: assessment.ref,
                      data: doc.data(),
                      rank: assessment.rank || 1,
                    };
                  }
                }
                return null;
              })
            );

            // Filter out any null results and set with proper ranking
            const validAssessments = fullAssessments
              .filter((assessment) => assessment !== null)
              .map((assessment, index) => ({
                ...assessment,
                rank: assessment.rank || index + 1,
              }));

            setSelectedAssessments(validAssessments);
          } catch (error) {
            console.error("Error loading assessments for editing:", error);
            setSelectedAssessments([]);
          }
        } else {
          setSelectedAssessments([]);
        }
      };

      loadAssessments();

      if (props.collection === "techStacks150r") {
        setTechData({ ...data });
      } else {
        setNewData({ ...data });
      }
    }
  }, [props.isEdit, props.defaultData?.id, props.collection, props.open]);

  const selectLogo = (e) => {
    const file = e.target.files[0];
    if (file?.type?.includes("image")) {
      setLogo({ file: file, url: URL.createObjectURL(file) });
    }
  };

  const uploadLogo = async (file, name) => {
    if (file) {
      let fileExt = file.type.replace("image/", "");
      let url;
      switch (name) {
        case "techStacks150r":
          const ref = await storage
            .ref(`techStacks/${techData.name}.${fileExt}`)
            .put(file);
          url = await ref.ref.getDownloadURL();
          return url;

        case "industry":
          const domRef = await storage
            .ref(`industry/${newData.name}.${fileExt}`)
            .put(file);
          url = await domRef.ref.getDownloadURL();
          return [url, `${newData.name}.${fileExt}`];

        case "subSectors":
          const subRef = await storage
            .ref(
              `sectors/${props.selectedSector.name}/${newData.name}.${fileExt}`
            )
            .put(file);
          url = await subRef.ref.getDownloadURL();
          return [url, `${newData.name}.${fileExt}`];

        case "categories":
          const projRef = await storage
            .ref(`projectCategories/${newData.name}.png`)
            .put(file);
          url = await projRef.ref.getDownloadURL();
          return [url, `${newData.name}`];

        default:
          break;
      }
    }
  };

  const addNewData = async (name) => {
    const { techCategory, ...stackData } = techData;
    const { category, ...others } = newData;

    let data = name === "techStacks150r" ? stackData : others;
    data["addedOn"] = new Date();

    if (data.name) {
      setLoading(true);
      try {
        const url = await uploadLogo(logo.file, props.collection);
        if (url) {
          if (name !== "techStacks150r") {
            data["logoStorage"] = url[0];
          } else {
            data["logoStorage"] = url;
          }

          // Add title field for all collection types for consistency
          data["title"] = data.name;

          if (name === "industry" || name === "subSectors") {
            data["image"] = url[1];
          }
          if (name === "categories") {
            data["iconImage"] = url[1];
          }

          let docRef;

          // Check if this is a sub-item (has a selectedSector/parent)
          if (props.selectedSector?.ref) {
            // For sub-items, use the parent ref to determine the subcollection path
            const parentRef = props.selectedSector.ref;
            console.log("Creating sub-item with parent ref:", parentRef.path);
            let subcollectionPath;

            switch (name) {
              case "techStacks150r":
                subcollectionPath = `${parentRef.path}/technologies`;
                break;
              case "industry":
                subcollectionPath = `${parentRef.path}/industry`;
                break;
              case "categories":
                subcollectionPath = `${parentRef.path}/subcollections`;
                break;
              case "subSectors":
                subcollectionPath = `${parentRef.path}/subSectors`;
                break;
              default:
                throw new Error(`Unknown subcollection type: ${name}`);
            }

            docRef = await props.firebase.createDocumentFromPath(
              subcollectionPath,
              data
            );
          } else {
            // For main collection items, create directly in the main collection
            console.log("Creating main collection item for:", name);
            switch (name) {
              case "techStacks150r":
                docRef = await props.firebase.createDocumentFromPath(
                  "techStacks150r",
                  data
                );
                break;
              case "categories":
                docRef = await props.firebase.createDocumentFromPath(
                  "categories",
                  data
                );
                break;
              case "industry":
                // For industry items, we need a selected sector
                if (!selectedSectorForIndustry?.ref) {
                  throw new Error(
                    "Industry items require a sector to be selected"
                  );
                }
                // Create industry item under the selected sector
                const industryPath = `${selectedSectorForIndustry.ref.path}/industry`;
                docRef = await props.firebase.createDocumentFromPath(
                  industryPath,
                  data
                );
                break;
              default:
                throw new Error(`Unknown main collection type: ${name}`);
            }
          }

          console.log("Created document:", docRef);
          return docRef;
        }
      } catch (error) {
        console.error("Error in addNewData:", error);
        throw error;
      }
    }
    return null;
  };

  const editData = async (name) => {
    let theData = techData.name ? techData : newData;
    const {
      catIndex,
      techCategory,
      category,
      popularQueries,
      nameIndex,
      stats,
      catData,
      catId,
      owner,
      ref,
      ...data
    } = theData;

    if (!logo.file) {
      setLoading(true);
      await ref.update(data);
      setLoading(false);
    } else {
      setLoading(true);
      const url = await uploadLogo(logo.file, props.collection);
      if (url) {
        data["logoStorage"] = name !== "techStacks150r" ? url[0] : url;
        data["title"] = data.name; // Add title for all collection types
        if (name === "industry" || name === "subSectors")
          data["image"] = url[1];
        if (name === "categories") data["iconImage"] = url[1];

        await ref.update(data);
        setLoading(false);
      }
    }
    return ref;
  };

  const onSave = async (name) => {
    if (loading) return;

    // Validation: Check if sector is selected for industry items without parent
    if (
      name === "industry" &&
      !props.selectedSector &&
      !selectedSectorForIndustry
    ) {
      // alert("Please select a sector for this industry item.");
      console.log("Please select a sector for this industry item.");
      return;
    }

    // console.log("selectedAssessments", selectedAssessments);
    // console.log("props.selectedAssessmentNames", props.selectedAssessmentNames);
    // console.log("props.collection", props.collection);
    // console.log("techData", techData);
    // console.log("newData", newData);
    // console.log("logo", logo);
    // console.log("name", name);
    // console.log("selectedSectorForIndustry", selectedSectorForIndustry);
    // console.log("props.isEdit", props.isEdit);
    // console.log("props.open", props.open);
    // console.log("props.defaultData", props.defaultData);
    // console.log("props.setAssessments", props.setAssessments);
    // console.log("props.refreshData", props.refreshData);

    try {
      setLoading(true);
      props?.setAssessments(selectedAssessments);
      const assessmentsForSave = selectedAssessments.map(({ ref, rank }) => ({
        ref,
        rank,
      }));

      const db = props.firebase.db;

      if (!props.isEdit) {
        if (logo.file) {
          let data = name === "techStacks150r" ? techData : newData;
          data.assessments = assessmentsForSave;

          // Save to original collection and get the new reference
          const newRef = await addNewData(name);
          // console.log("newRef", newRef);
          if (!newRef) {
            throw new Error("Failed to create new document");
          }

          // Create a batch for all updates including the initial document
          const batch = db.batch();

          // Update the document with assessments
          batch.update(newRef, {
            assessments: assessmentsForSave,
          });

          // Prepare all catalog updates
          selectedAssessments.forEach((assessment) => {
            batch.update(assessment.ref, {
              skills: arrayUnion({
                ref: newRef,
                rank: assessment.rank,
              }),
            });
          });

          // Execute all updates atomically
          await batch.commit();

          props.refreshData();
          resetStates(() => setLoading(false)); // Pass setLoading as callback
        }
      } else {
        let data = name === "techStacks150r" ? techData : newData;
        data.assessments = assessmentsForSave;

        try {
          // First update the document and handle logo if needed
          const docRef = await editData(name);
          // console.log("docRef", docRef);
          // Then handle the assessment references in a transaction
          await db.runTransaction(async (transaction) => {
            // READS FIRST
            // 1. Get the document snapshot
            const docSnapshot = await transaction.get(docRef);
            const previousAssessments = docSnapshot.data().assessments || [];

            // Find removed assessments first
            const removedAssessments = previousAssessments.filter(
              (prevAssessment) =>
                !selectedAssessments.some(
                  (current) => current.ref.path === prevAssessment.ref.path
                )
            );

            // 2. Get all catalog documents we need to update
            const catalogDocs = await Promise.all([
              ...removedAssessments.map((assessment) =>
                transaction.get(assessment.ref)
              ),
              ...selectedAssessments.map((assessment) =>
                transaction.get(assessment.ref)
              ),
            ]);

            // Prepare the updates
            const updates = [];

            // THEN WRITES
            // 1. Update the main document
            updates.push(() =>
              transaction.update(docRef, {
                assessments: assessmentsForSave,
              })
            );

            // 2. Handle removed assessments
            removedAssessments.forEach((assessment, index) => {
              const catalogDoc = catalogDocs[index];
              const catalogData = catalogDoc.data();

              if (catalogData.skills) {
                const updatedSkills = catalogData.skills.filter(
                  (skill) => skill.ref.path !== docRef.path
                );

                updates.push(() =>
                  transaction.update(assessment.ref, {
                    skills:
                      updatedSkills.length > 0 ? updatedSkills : deleteField(),
                  })
                );
              }
            });

            // 3. Handle selected assessments
            selectedAssessments.forEach((assessment, index) => {
              const catalogDoc = catalogDocs[removedAssessments.length + index];
              const catalogData = catalogDoc.data();

              let currentSkills = catalogData.skills || [];
              currentSkills = currentSkills.filter(
                (skill) => skill.ref.path !== docRef.path
              );
              currentSkills.push({
                ref: docRef,
                rank: assessment.rank,
              });

              updates.push(() =>
                transaction.update(assessment.ref, {
                  skills: currentSkills,
                })
              );
            });

            // Execute all updates
            updates.forEach((update) => update());
          });

          props.refreshData();
          resetStates(() => setLoading(false)); // Pass setLoading as callback
        } catch (error) {
          console.error("Error updating data:", error);
        }
      }
    } catch (error) {
      console.error("Error in onSave:", error);
      setLoading(false); // Only set loading false immediately on error
    }
  };

  const toggleAssessment = (assessment) => {
    // console.log("toggleAssessment called with assessment:", assessment);

    // Safety check for assessment structure
    if (!assessment?.ref?.path) {
      console.error("Invalid assessment structure:", assessment);
      return;
    }

    setSelectedAssessments((prev) => {
      const exists = prev.some(
        (item) => item?.ref?.path === assessment.ref.path
      );

      if (exists) {
        // Remove assessment and reassign ranks
        const updated = prev
          .filter((item) => item?.ref?.path !== assessment.ref.path)
          .map((item, index) => ({ ...item, rank: index + 1 }));
        // console.log("Updated selectedAssessments after removal:", updated);
        return updated;
      } else {
        // Add assessment with new rank
        const updated = [...prev, { ...assessment, rank: prev.length + 1 }];
        // console.log("Updated selectedAssessments after addition:", updated);
        return updated;
      }
    });
  };

  const onRemoveAssessment = (assessment) => {
    // console.log("onRemoveAssessment called with assessment:", assessment);

    // Safety check for assessment structure
    if (!assessment?.ref?.path) {
      console.error("Invalid assessment structure:", assessment);
      return;
    }

    setSelectedAssessments((prev) => {
      const updated = prev
        .filter((item) => item?.ref?.path !== assessment.ref.path)
        .map((item, index) => ({ ...item, rank: index + 1 }));
      // console.log("Updated selectedAssessments after removal:", updated);
      return updated;
    });
  };

  // console.log({
  //   techData,
  //   newData,
  // });

  // Determine if we're working with a sub-item vs main collection item
  const isSubItem = () => {
    // Simply check if we have a parent (selectedSector with ref)
    return props.selectedSector?.ref ? true : false;
  };

  // Determine if we should show category dropdown
  const shouldShowCategoryDropdown = () => {
    // For main collection items, don't show dropdown (they ARE the category)
    if (!isSubItem()) return false;

    // For sub-items, we don't need dropdown since we use the parent ref directly
    // The parent is already selected when creating sub-items
    return false;
  };

  // Form validation function
  const isFormValid = () => {
    const currentData =
      props.collection === "techStacks150r" ? techData : newData;

    // Name is always required
    if (!currentData.name?.trim()) {
      return false;
    }

    // Logo is required for new items (not when editing)
    if (!props.isEdit && !logo.file && !currentData.logoStorage) {
      return false;
    }

    // For industry items that are main collection items (not sub-items), sector selection is required
    if (
      props.collection === "industry" &&
      !isSubItem() &&
      !selectedSectorForIndustry
    ) {
      return false;
    }

    return true;
  };

  const getNew = () => {
    switch (props.collection) {
      case "techStacks150r":
        return (
          <NewStack
            techsCategories={props.techsCategories}
            setTechData={setTechData}
            techData={techData}
            handleOpen={props.handleFileUpload}
            selectLogo={selectLogo}
            logo={logo}
            setLogo={setLogo}
            openDialog={shouldShowCategoryDropdown()}
            isEdit={props.isEdit}
            editData={props.defaultData}
            show={showOptions}
            defaultData={props.defaultData}
            searchAssessments={props.searchAssessments}
            searchData={props.searchData}
            setSearchData={props.setSearchData}
            showDropdown={props.showDropdown}
            setShowDropdown={props.setShowDropdown}
            selectedAssessmentNames={props.selectedAssessmentNames}
            onToggleAssessment={props.onToggleAssessment}
            selectedAssessments={selectedAssessments}
            toggleAssessment={toggleAssessment}
            onRemoveAssessment={onRemoveAssessment}
            isSub={isSubItem()}
          />
        );

      case "categories":
        return (
          <NewProj
            techsCategories={props.techsCategories}
            setNewData={setNewData}
            newData={newData}
            handleOpen={props.handleFileUpload}
            selectLogo={selectLogo}
            logo={logo}
            setLogo={setLogo}
            openDialog={shouldShowCategoryDropdown()}
            isEdit={props.isEdit}
            editData={props.defaultData}
            show={showOptions}
            onToggleAssessment={props.onToggleAssessment}
            searchAssessments={props.searchAssessments}
            searchData={props.searchData}
            setSearchData={props.setSearchData}
            showDropdown={props.showDropdown}
            setShowDropdown={props.setShowDropdown}
            selectedAssessmentNames={props.selectedAssessmentNames}
            toggleAssessment={toggleAssessment}
            onRemoveAssessment={onRemoveAssessment}
            selectedAssessments={selectedAssessments}
            isSub={isSubItem()}
          />
        );

      default:
        return (
          <NewDomain
            techsCategories={props.techsCategories}
            setNewData={setNewData}
            newData={newData}
            handleOpen={props.handleFileUpload}
            selectLogo={selectLogo}
            logo={logo}
            setLogo={setLogo}
            openDialog={shouldShowCategoryDropdown()}
            isEdit={props.isEdit}
            editData={props.defaultData}
            show={showOptions}
            isSub={isSubItem()}
            onToggleAssessment={props.onToggleAssessment}
            searchAssessments={props.searchAssessments}
            searchData={props.searchData}
            setSearchData={props.setSearchData}
            showDropdown={props.showDropdown}
            setShowDropdown={props.setShowDropdown}
            selectedAssessmentNames={props.selectedAssessmentNames}
            toggleAssessment={toggleAssessment}
            onRemoveAssessment={onRemoveAssessment}
            selectedAssessments={selectedAssessments}
            // Sector selection props for industry items
            collection={props.collection}
            availableSectors={availableSectors}
            selectedSectorForIndustry={selectedSectorForIndustry}
            setSelectedSectorForIndustry={setSelectedSectorForIndustry}
            loadingSectors={loadingSectors}
          />
        );
    }
  };

  return (
    <SpDialog
      open={props?.open}
      locked={true}
      onClose={props?.onClose}
      useCustomBox={true}
    >
      <div
        className="collectionModal column modal-content"
        id="col_data-request_form"
      >
        {getNew()}
        <div className="request_form-action row">
          <button
            className="spbtn spbtn-sub"
            onClick={() => onSave(props.collection)}
            disabled={loading || !isFormValid()}
          >
            {loading ? (
              <div className="button-content">
                <CircularProgress size={20} color="inherit" />
                <span style={{ marginLeft: "8px" }}>Saving...</span>
              </div>
            ) : (
              "Save"
            )}
          </button>
          <button
            className="spbtn spbtn-canc"
            onClick={() => resetStates(() => {})}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </SpDialog>
  );
};

export default AddNew;
